export interface NotificationChannel {
  id: string;
  scope: string;
  tenant_id?: string;
  channel_key: string;
  channel_type: string;
  display_name: string;
  enabled: boolean;
  default_from_email?: string;
  config: {
    host?: string;
    port?: number;
    secure?: boolean;
    from_email?: string;
  };
  secret_configured: boolean;
  created_at: string;
  updated_at: string;
}

export interface NotificationRoute {
  id: string;
  scope: string;
  tenant_id?: string;
  event_type: string;
  notification_type: string;
  channel_id: string;
  channel_key: string;
  channel_type: string;
  channel_display_name: string;
  enabled: boolean;
  priority: number;
  filter_json?: Record<string, unknown>;
  target_count: number;
  targets: NotificationRouteTarget[];
  created_at: string;
  updated_at: string;
}

export interface NotificationRouteTarget {
  id: string;
  route_id: string;
  channel_id: string;
  target_kind: 'recipient_group' | 'customer_email';
  recipient_group_id?: string;
  recipient_group_key?: string;
  recipient_group_name?: string;
  target_ref?: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface NotificationRecipientGroup {
  id: string;
  scope: string;
  tenant_id?: string;
  group_key: string;
  display_name: string;
  enabled: boolean;
  member_count: number;
  members: NotificationRecipientGroupMember[];
  created_at: string;
  updated_at: string;
}

export interface NotificationRecipientGroupMember {
  id: string;
  group_id: string;
  member_kind: 'email';
  display_name?: string;
  email: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface NotificationDelivery {
  id: string;
  event_id: string;
  event_type: string;
  aggregate_type: string;
  aggregate_id: string;
  notification_type: string;
  tenant_id?: string;
  user_uid?: string;
  channel_id?: string;
  channel_key: string;
  target_kind: string;
  recipient_email: string;
  recipient_name?: string;
  status: string;
  attempt_count: number;
  last_attempt_at?: string;
  provider_message_id?: string;
  sent_at?: string;
  error_message?: string;
  created_at: string;
  updated_at?: string;
}

export interface NotificationListResponse<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
  sort: string;
  direction: string;
}

export interface NotificationChannelListParams {
  limit?: number;
  offset?: number;
  enabled?: boolean;
  channelType?: string;
  q?: string;
  sort?: 'created_at' | 'updated_at' | 'display_name' | 'channel_key';
  direction?: 'asc' | 'desc';
}

export interface NotificationRouteListParams {
  limit?: number;
  offset?: number;
  enabled?: boolean;
  eventType?: string;
  channelId?: string;
  q?: string;
  sort?: 'priority' | 'created_at' | 'updated_at' | 'event_type';
  direction?: 'asc' | 'desc';
}

export interface NotificationDeliveryListParams {
  limit?: number;
  offset?: number;
  status?: string;
  notificationType?: string;
  eventType?: string;
  channelId?: string;
  recipient?: string;
  sort?: 'created_at' | 'updated_at' | 'last_attempt_at' | 'status';
  direction?: 'asc' | 'desc';
}

export interface NotificationChannelPayload {
  channel_key: string;
  channel_type: string;
  display_name: string;
  enabled: boolean;
  default_from_email?: string;
  config: {
    host?: string;
    port?: number;
    secure?: boolean;
    from_email?: string;
  };
  secret_payload?: {
    username?: string;
    password?: string;
  };
}

export interface NotificationRoutePayload {
  event_type: string;
  notification_type: string;
  channel_id: string;
  enabled: boolean;
  priority: number;
  filter_json?: Record<string, unknown>;
  targets: NotificationRouteTargetPayload[];
}

export interface NotificationRouteTargetPayload {
  target_kind: 'recipient_group' | 'customer_email';
  recipient_group_id?: string;
  target_ref?: string;
  enabled: boolean;
}

export interface NotificationRecipientGroupListParams {
  limit?: number;
  offset?: number;
  enabled?: boolean;
  q?: string;
  sort?: 'created_at' | 'updated_at' | 'display_name' | 'group_key';
  direction?: 'asc' | 'desc';
}

export interface NotificationRecipientGroupPayload {
  group_key: string;
  display_name: string;
  enabled: boolean;
  members: NotificationRecipientGroupMemberPayload[];
}

export interface NotificationRecipientGroupMemberPayload {
  member_kind: 'email';
  display_name?: string;
  email: string;
  enabled: boolean;
}

export class NotificationAdminError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'NotificationAdminError';
    this.status = status;
  }
}

async function readJSON<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  });

  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    try {
      const body = await response.json() as { detail?: string; title?: string };
      message = body.detail || body.title || message;
    } catch {
      // ignore body parsing failures
    }
    throw new NotificationAdminError(message, response.status);
  }

  return readJSON<T>(response);
}

function buildQueryString(params: Record<string, string | number | boolean | undefined | null>): string {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    query.set(key, String(value));
  });
  const encoded = query.toString();
  return encoded ? `?${encoded}` : '';
}

export async function listNotificationChannels(params: NotificationChannelListParams = {}): Promise<NotificationListResponse<NotificationChannel>> {
  return request<NotificationListResponse<NotificationChannel>>(
    `/api/v1/notifications/channels${buildQueryString({
      limit: params.limit,
      offset: params.offset,
      enabled: params.enabled,
      channel_type: params.channelType,
      q: params.q,
      sort: params.sort,
      direction: params.direction,
    })}`,
  );
}

export async function createNotificationChannel(payload: NotificationChannelPayload): Promise<NotificationChannel> {
  return request<NotificationChannel>('/api/v1/notifications/channels', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateNotificationChannel(channelId: string, payload: NotificationChannelPayload): Promise<NotificationChannel> {
  return request<NotificationChannel>(`/api/v1/notifications/channels/${encodeURIComponent(channelId)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function testNotificationChannel(channelId: string, recipientEmail: string, recipientName?: string): Promise<void> {
  await request(`/api/v1/notifications/channels/${encodeURIComponent(channelId)}/test`, {
    method: 'POST',
    body: JSON.stringify({
      recipient_email: recipientEmail,
      recipient_name: recipientName,
    }),
  });
}

export async function listNotificationRoutes(params: NotificationRouteListParams = {}): Promise<NotificationListResponse<NotificationRoute>> {
  return request<NotificationListResponse<NotificationRoute>>(
    `/api/v1/notifications/routes${buildQueryString({
      limit: params.limit,
      offset: params.offset,
      enabled: params.enabled,
      event_type: params.eventType,
      channel_id: params.channelId,
      q: params.q,
      sort: params.sort,
      direction: params.direction,
    })}`,
  );
}

export async function listNotificationRecipientGroups(params: NotificationRecipientGroupListParams = {}): Promise<NotificationListResponse<NotificationRecipientGroup>> {
  return request<NotificationListResponse<NotificationRecipientGroup>>(
    `/api/v1/notifications/recipient-groups${buildQueryString({
      limit: params.limit,
      offset: params.offset,
      enabled: params.enabled,
      q: params.q,
      sort: params.sort,
      direction: params.direction,
    })}`,
  );
}

export async function createNotificationRecipientGroup(payload: NotificationRecipientGroupPayload): Promise<NotificationRecipientGroup> {
  return request<NotificationRecipientGroup>('/api/v1/notifications/recipient-groups', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateNotificationRecipientGroup(groupId: string, payload: NotificationRecipientGroupPayload): Promise<NotificationRecipientGroup> {
  return request<NotificationRecipientGroup>(`/api/v1/notifications/recipient-groups/${encodeURIComponent(groupId)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function deleteNotificationRecipientGroup(groupId: string): Promise<void> {
  await request(`/api/v1/notifications/recipient-groups/${encodeURIComponent(groupId)}`, {
    method: 'DELETE',
  });
}

export async function createNotificationRoute(payload: NotificationRoutePayload): Promise<NotificationRoute> {
  return request<NotificationRoute>('/api/v1/notifications/routes', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateNotificationRoute(routeId: string, payload: NotificationRoutePayload): Promise<NotificationRoute> {
  return request<NotificationRoute>(`/api/v1/notifications/routes/${encodeURIComponent(routeId)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function deleteNotificationRoute(routeId: string): Promise<void> {
  await request(`/api/v1/notifications/routes/${encodeURIComponent(routeId)}`, {
    method: 'DELETE',
  });
}

export async function listNotificationDeliveries(params: NotificationDeliveryListParams = {}): Promise<NotificationListResponse<NotificationDelivery>> {
  return request<NotificationListResponse<NotificationDelivery>>(
    `/api/v1/notifications/deliveries${buildQueryString({
      limit: params.limit,
      offset: params.offset,
      status: params.status,
      notification_type: params.notificationType,
      event_type: params.eventType,
      channel_id: params.channelId,
      recipient: params.recipient,
      sort: params.sort,
      direction: params.direction,
    })}`,
  );
}
